<template>
    <div>
        <rkm-table
            :fields="fields"
            :items="data.model.agendamentos"
            :tbody-tr-class="rowClass"
            ref="RkmTable"
            responseive
            sort-by="dataHorario"
        >
            <template v-slot:cell(pacienteNome)="{index, item}">
                <template v-if="item.pacienteNome">
                    <rkm-button
                        :id="uniqueId(`paciente-icon-${index}`)"
                        class="mr-2"
                        small
                    >
                        <rkm-icon icon="user" />
                    </rkm-button>

                    <rkm-popover
                        :target="uniqueId(`paciente-icon-${index}`)"
                        placement="top"
                        title="Dados cadastrais"
                        triggers="hover"
                    >
                        <div>
                            <span>
                                <strong>Data de nascimento:</strong> {{item.pacienteDataNascimento}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>Nome da mãe:</strong> {{item.pacienteNomeMae}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>CPF:</strong> {{item.pacienteCpf}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>CNS:</strong> {{item.pacienteCns}}
                            </span>
                        </div>
                    </rkm-popover>

                    <rkm-popover
                        :target="uniqueId(`paciente-observacao-icon-${index}`)"
                        placement="top"
                        title="Observação do agendamento"
                        triggers="hover"
                    >
                        <div v-if="item.observacao">
                            {{ item.observacao }}
                        </div>
                        <div class="text-danger" v-else>
                            Agendamento sem observação
                        </div>
                    </rkm-popover>

                    <template>
                        {{item.pacienteNome}}

                        <template v-if="item.pacientePreferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            <strong>
                                ({{item.pacienteNomeSocial}})
                            </strong>
                        </template>
                    </template>
                </template>
            </template>

            <template v-slot:cell(actions)="{index, item}">
                <rkm-button
                        :id="uniqueId(`paciente-observacao-icon-${index}`)"
                        color="secondary"
                        small
                    >
                        <rkm-icon icon="info" />
                    </rkm-button>
                <template v-if="item.statusId === enums.agendamentoStatus.AGENDADO">
                    <rkm-button
                        :disabled="!item.permiteConfirmarChegada"
                        @click="$refs.ModalChegada.open(item.id)"
                        color="primary"
                        small
                        title="Registrar chegada do paciente"
                    >
                        <rkm-icon icon="door-open" />
                    </rkm-button>

                    <rkm-button
                        :disabled="!item.podeReagendar || !verificarPermissao('update-ambulatorio-agendamento')"
                        @click="reagendar(item)"
                        color="info"
                        small
                        :title="verificarPermissao('update-ambulatorio-agendamento')
                            ? 'Reagendar'
                            : 'O usuário logado não tem permissão para reagendar.'"
                    >
                        <rkm-icon icon="calendar-alt" />
                    </rkm-button>

                    <rkm-button
                        @click="absent(item.id)"
                        color="warning"
                        small
                        title="Registrar falta"
                    >
                        <rkm-icon icon="exclamation" />
                    </rkm-button>

                    <ModalAgendamento
                        :paciente-id="item.pacienteId"
                        :ref="`ModalAgendamento${item.pacienteId}`"
                        @updated="$emit('saved')"
                        disable-filters
                    />
                </template>

                <rkm-button
                    v-else-if="item.statusId === enums.agendamentoStatus.FALTA_DO_PACIENTE"
                    :disabled="!item.podeDesfazerFalta"
                    @click="undoAbsent(item.id)"
                    color="warning"
                    small
                    title="Cancelar registro de falta"
                >
                    <rkm-icon icon="undo" />
                </rkm-button>
            </template>
        </rkm-table>

        <ModalChegada
            @saved="$emit('saved')"
            ref="ModalChegada"
        />
    </div>
</template>

<script>
// Components.
import ModalChegada from './components/chegada/Modal'
import ModalAgendamento from '@/components/ambulatorio/agendamento/components/cadastro/Modal'

// Enums.
import agendamentoStatus from '@/enums/ambulatorio/agendamentoStatus'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'

// Mixins.
import {error, formats, permissao, repository} from '@/mixins'

// Moment
import moment from 'moment'

export default {
    components: {ModalChegada, ModalAgendamento},
    computed: {
        fields() {
            return [
                {
                    formatter: value => this.others.moment(value).format('DD/MM/YYYY HH:mm'),
                    filterByFormatted: true,
                    key: 'dataHorario',
                    label: 'Data / Hora da consulta',
                    sortable: true
                },
                {key: 'tempoAtraso', label: 'Atraso', sortable: true},
                {key: 'pacienteNome', label: 'Nome', sortable: true},
                {key: 'pacienteIdadeAnos', label: 'Idade', sortable: true},
                {key: 'pacienteProntuario', label: 'Prontuário', sortable: true},
                {key: 'pacienteSexoDescricao', label: 'Sexo', sortable: true},
                {key: 'especialidadeNome', label: 'Especialidade', sortable: true},
                {key: 'usuarioNome', label: 'Profissional', sortable: true},
                {class: 'col-min text-center', key: 'actions', label: 'Ações'},
            ]
        },
    },
    created() {
        this.repositories = {
            agendamento: this.getConectaSusRepository('ambulatorio.agendamento'),
        }
    },
    data: () => ({
        enums: {
            agendamentoStatus,
            preferenciaNome,
        },
        others: {moment},
    }),
    methods: {
        reagendar(item) {
            if (item.cadastroPacienteVencido) {
                this.showWarningNotification(`Paciente com cadastro temporário desatualizado. Agendamento não permitido.`)
            }
            else if (!item.cnsValido) {
                this.showWarningNotification('Paciente com CNS inválido. Agendamento não permitido.')
            }
            else {
                if (item.avisaCnsProvisorio) {
                    this.showWarningNotification('Paciente com CNS provisório.')
                }

                this.$refs[`ModalAgendamento${item.pacienteId}`].edit(item.id)
            }
        },
        absent(agendamentoId) {
            const registrarFalta = () => this.repositories.agendamento.absent(agendamentoId)
                .then(() => this.$emit('saved'))
                .catch(this.handleError)

            this.showWarningConfirmation(
                'Registro de falta',
                'Deseja realmente registrar a falta do paciente?',
                registrarFalta,
            )
        },
        undoAbsent(agendamentoId) {
            const undoAbsent = () => this.repositories.agendamento.undoAbsent(agendamentoId)
                .then(() => this.$emit('saved'))
                .catch(this.handleError)

            this.showWarningConfirmation(
                'Cancelar registro de falta',
                'Deseja realmente cancelar o registro de falta do paciente?',
                undoAbsent,
            )
        },
        rowClass(item, type) {
            if (type === 'row' && item && item.statusId === this.enums.agendamentoStatus.FALTA_DO_PACIENTE) {
                return 'table-danger'
            }
        },
    },
    mixins: [error, formats, permissao, repository],
    props: {
        data: {type: Object, required: true},
    },
}
</script>